import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardDescription } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import { ArrowLeft, Mail, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Por favor ingresa tu correo electrónico');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/password-reset/request`, { email });
      setSent(true);
      toast.success('Instrucciones enviadas');
    } catch (err) {
      toast.error('Error al procesar solicitud');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary/30 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/">
            <img src="/logo-kind.png" alt="KIND TECHNOLOGIES" className="h-16 w-auto mx-auto mb-4" />
          </Link>
          <h1 className="text-3xl font-display font-semibold tracking-tight">
            Recuperar Contraseña
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Ingresa tu correo para recibir instrucciones
          </p>
        </div>

        <Card>
          <CardContent className="p-6">
            {!sent ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Correo Electrónico</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="tu@correo.com"
                      className="pl-10"
                      disabled={loading}
                      data-testid="forgot-password-email-input"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={loading}
                  data-testid="forgot-password-submit-button"
                >
                  {loading ? 'Enviando...' : 'Enviar Instrucciones'}
                </Button>

                <div className="text-center">
                  <Link 
                    to="/login" 
                    className="text-sm text-muted-foreground hover:text-foreground flex items-center justify-center gap-1"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Volver al inicio de sesión
                  </Link>
                </div>
              </form>
            ) : (
              <div className="space-y-4 text-center">
                <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">¡Revisa tu correo!</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Si el correo <strong>{email}</strong> está registrado en nuestro sistema,
                    recibirás instrucciones para restablecer tu contraseña.
                  </p>
                  <Alert>
                    <AlertDescription className="text-xs">
                      No olvides revisar tu carpeta de spam o correo no deseado.
                    </AlertDescription>
                  </Alert>
                </div>
                <Link to="/login">
                  <Button variant="outline" className="w-full">
                    Volver al inicio de sesión
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="mt-4 text-center text-xs text-muted-foreground">
          ¿No tienes una cuenta?{' '}
          <Link to="/register" className="text-foreground hover:underline">
            Regístrate aquí
          </Link>
        </p>
      </div>
    </div>
  );
}
